/**
 * Color helpers shared across components that need to compose CSS colors at
 * runtime (drop-shadows, gradient washes, scrim overlays, etc.).
 *
 * Why a tiny utility instead of inline duplicates: there were three nearly
 * identical copies of `hexToRgba` (OrbAvatar, DesktopLayout, LockScreen).
 * Each had small drift in the fallback path that made it hard to be sure
 * whether one was a bugfix or a forgotten copy. Centralising here keeps a
 * single source of truth — callers handle their own missing-input fallback
 * since the right fallback is context-specific (brand blue for the orb,
 * pass-through for theme tokens, etc.).
 */

/**
 * Convert a `#rrggbb` or `#rgb` hex literal to `rgba(r,g,b,alpha)`.
 *
 * Non-hex strings pass through unchanged so callers can hand the result to
 * any CSS color context (`rgb(...)`, `hsl(...)`, named colors, currentColor,
 * etc.) without losing those values. `null` / `undefined` / empty string
 * resolve to `''` so the caller's `||` fallback path runs predictably.
 */
export function hexToRgba(hex: string | null | undefined, alpha: number): string {
  if (!hex || typeof hex !== 'string') return '';
  if (!hex.startsWith('#')) return hex;
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return hex;
  return `rgba(${r},${g},${b},${alpha})`;
}
